import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import type { Lot } from '../data/mockLots';
import { api } from '../lib/apiClient';
import { type ClosedLotsResponse, type LotsListResponse, lotFromApi } from '../lib/lots';

const LIST_STALE_MS = 5_000;
// Kept in lockstep with the server-side list cache TTL (LOTS_LIST_CACHE_TTL_MS):
// polling faster than the cache refreshes only re-reads the same snapshot.
const LIVE_REFETCH_MS = 1_000;

export type LotsBuckets = {
  live: Lot[];
  scheduled: Lot[];
  // The server-chosen hero (selectHero). Self-contained: for the last-win case
  // it is a closed lot absent from `live`/`scheduled`, so the home screen renders
  // it directly rather than resolving it from a bucket.
  hero: Lot | null;
};

async function fetchDefaultBuckets(): Promise<LotsBuckets> {
  const res = await api.get<LotsListResponse>('/api/lots');
  const live: Lot[] = [];
  const scheduled: Lot[] = [];
  for (const item of res.lots) {
    const lot = lotFromApi(item);
    if (item.status === 'live') live.push(lot);
    else if (item.status === 'scheduled') scheduled.push(lot);
  }
  // Deterministic order so cards don't reshuffle between polls: by start time
  // (live → startedAt, scheduled → startsAt), id as a stable tie-break. The API
  // response order isn't guaranteed stable across polls, so we own the order here.
  live.sort((a, b) => (a.startedAt ?? 0) - (b.startedAt ?? 0) || (a.id < b.id ? -1 : 1));
  scheduled.sort((a, b) => (a.startsAt ?? 0) - (b.startsAt ?? 0) || (a.id < b.id ? -1 : 1));
  const hero = res.hero ? lotFromApi(res.hero.lot) : null;
  return { live, scheduled, hero };
}

export type ClosedLots = {
  // Up to the server cap (100), newest first — what the home screen reveals.
  lots: Lot[];
  // Platform-wide count of played lots; can exceed lots.length once the archive
  // outgrows the cap, so the section header reads from here, not lots.length.
  total: number;
};

async function fetchClosedLots(): Promise<ClosedLots> {
  const res = await api.get<ClosedLotsResponse>('/api/lots?status=closed');
  return { lots: res.lots.map(lotFromApi), total: res.total };
}

// `active` pauses the live poll while a lot detail is open — that screen has its
// own per-lot WS and never reads this list, so polling it is pure waste (and on a
// closed lot, there is nothing to refresh). Cached data is kept, so returning to
// the home is instant and refetches only if stale.
export function useLots(active = true): UseQueryResult<LotsBuckets> {
  return useQuery({
    queryKey: ['lots', 'default'],
    queryFn: fetchDefaultBuckets,
    staleTime: LIST_STALE_MS,
    refetchInterval: active ? LIVE_REFETCH_MS : false,
  });
}

// A lot only ever leaves the `live` bucket by closing (live never reverts to
// scheduled), so any id present in the previous live set but gone from the
// current one is a freshly-closed lot — the cue to refresh the closed list,
// which has no poll of its own.
export function liveLotClosed(
  prevLiveIds: Iterable<string>,
  currentLiveIds: Iterable<string>,
): boolean {
  const current = new Set(currentLiveIds);
  for (const id of prevLiveIds) {
    if (!current.has(id)) return true;
  }
  return false;
}

export function useClosedLots(): UseQueryResult<ClosedLots> {
  return useQuery({
    queryKey: ['lots', 'closed'],
    queryFn: fetchClosedLots,
    staleTime: 60_000,
  });
}
